import React, { useState, useCallback } from 'react';
import ReactLoading from 'react-loading';
import { BeakerIcon, BellIcon, BriefcaseIcon, BroadcastIcon, ContainerIcon, DatabaseIcon, DownloadIcon, GearIcon, GraphIcon,
  HomeIcon, OrganizationIcon, PackageIcon, PersonIcon, 
  SignOutIcon, SmileyIcon, SyncIcon, ToolsIcon, VersionsIcon } from '@primer/octicons-react';
import history from '../../history';
import { graphql, get, post, postAsync, postAsyncMultiple } from '../../post';
import s from './Navigation.css';

/**
 * Hook personnalisé pour la logique du composant Navigation
 * Gère l'état des menus, les actions asynchrones et la navigation
 * 
 * @param {Object} props - Props du composant
 * @param {Object} props.me - Utilisateur connecté
 * @param {Function} props.emitEvent - Fonction d'émission d'événements
 * @param {Function} props.t - Fonction de traduction
 * @returns {Object} État et fonctions du composant Navigation
 */
export const useNavigationLogic = ({ me, emitEvent, t } = {}) => {
  const [state, setState] = useState({
    loadUpdateAlarm: false,
    loadUpdateAutoMeter: false,
    loadUpdateAutoTable: false,
    loadUpdateLocate: false,
    loadSendAlarmEmail: false,
    menuOpened: undefined
  });

  /**
   * Gère les clics sur la fenêtre pour fermer les menus
   */
  const handleWindowClick = useCallback((e) => {
    const menu = e.target.closest("." + s.navMenuShow);
    if (!menu) {
      setState(prev => ({ ...prev, menuOpened: undefined }));
    }
  }, []);

  /**
   * Gère l'ouverture/fermeture des menus et la navigation
   */
  const handleMenu = useCallback((e, key, link, fct) => {
    e && e.stopPropagation();

    setState(prev => ({
      ...prev,
      menuOpened: prev.menuOpened === key ? undefined : key
    }));

    // Exécuter les actions après la mise à jour de l'état
    if (link) {
      history.push(link);
    }
    if (fct) {
      fct();
    }
  }, []);

  /**
   * Met à jour la géolocalisation
   */
  const updateGeoLocation = useCallback(() => {
    console.log("🔍 [updateGeoLocation] updateGeoLocation", state);
    if (state.loadUpdateLocate) {
      console.log("🔍 [updateGeoLocation] already in progress");
      emitEvent?.("alert", {
        text: t("navigation.tools.updategeolocation.alreadyinprogress")
      });
    } else {
      console.log("🔍 [updateGeoLocation] start update");
      setState(prev => ({ ...prev, loadUpdateLocate: true }));
      post("/updateLocateMissing")
        .then(res => {
          setState(prev => ({ ...prev, loadUpdateLocate: false }));
          emitEvent?.("alert", { text: t("navigation.tools.updategeolocation.done") });
        });
    }
  }, [state.loadUpdateLocate, emitEvent, t]);

  /**
   * Version asynchrone de updateAutoMeter avec callbacks
   */
  const updateAutoMeterAsync = useCallback(async () => {
    if (state.loadUpdateAutoMeter) {
      emitEvent?.("alert", {
        text: t("navigation.tools.syncautometer.alreadyinprogress")
      });
    } else {
      setState(prev => ({ ...prev, loadUpdateAutoMeter: true }));
      
      let totalNb = 0;
      
      try {
        await postAsyncMultiple(
          ["/syncautometerwebdyn", "/syncautosupervisionwebdyn", "/syncautometerlora"],
          null, null, null,
          {
                 onProgress: ({ status, progress, message, requestIndex, totalRequests, requestUrl }) => {
                   // Progression silencieuse
                 },
                 onSuccess: (result, { requestIndex, totalRequests, requestUrl }) => {
                   if (result && result.data && result.data.addMeterReading) {
                     totalNb += Object.keys(result.data.addMeterReading).length;
                   }
                   if (result && result.data && result.data.addSuppervisionReading) {
                     totalNb += Object.keys(result.data.addSuppervisionReading).length;
                   }
                 },
                 onError: (error, { requestIndex, totalRequests, requestUrl }) => {
                   // Erreur gérée silencieusement
                 },
                 onAllComplete: ({ successful, errors, total, successCount, errorCount }) => {
              
              setState(prev => ({ ...prev, loadUpdateAutoMeter: false }));
              
              if (successCount > 0) {
                emitEvent?.("alert", {
                  text: t("navigation.tools.syncautometer.done", { count: totalNb })
                });
              } else {
                emitEvent?.("alert", {
                  text: t("navigation.tools.syncautometer.error")
                });
              }
            }
          }, {
            maxAttempts: 300, // 300 tentatives × 5 secondes = 25 minutes
            interval: 5000,    // 5 secondes entre chaque vérification
            timeout: 300000      // 300000 ms = 5 minutes
          }
        );
      } catch (error) {
        console.error("❌ Erreur lors de l'import des télérelevés:", error);
        setState(prev => ({ ...prev, loadUpdateAutoMeter: false }));
        emitEvent?.("alert", {
          text: t("navigation.tools.syncautometer.error")
        });
      }
    }
  }, [state.loadUpdateAutoMeter, emitEvent, t]);

  /**
   * Met à jour les alarmes de manière asynchrone
   */
  const updateAlarmAsync = useCallback(async () => {
    if (state.loadUpdateAlarm) {
      emitEvent?.("alert", {
        text: t("navigation.tools.updatealarm.alreadyinprogress")
      });
    } else {
      setState(prev => ({ ...prev, loadUpdateAlarm: true }));
      
      try {
        await postAsync("/updateAlarm", null, null, null, {
                 onSuccess: (result) => {
                   setState(prev => ({ ...prev, loadUpdateAlarm: false }));
                   emitEvent?.("alert", { text: t("navigation.tools.updatealarm.done") });
                 },
                 onError: (error) => {
                   setState(prev => ({ ...prev, loadUpdateAlarm: false }));
                   emitEvent?.("alert", { text: t("navigation.tools.updatealarm.error") });
                 }
        }, {
          maxAttempts: 300, // 300 tentatives × 5 secondes = 25 minutes
          interval: 5000,    // 5 secondes entre chaque vérification
          timeout: 300000      // 300000 ms = 5 minutes
        });
      } catch (error) {
        console.error("❌ Erreur lors de la mise à jour des alarmes:", error);
        setState(prev => ({ ...prev, loadUpdateAlarm: false }));
        emitEvent?.("alert", {
          text: t("navigation.tools.updatealarm.error")
        });
      }
    }
  }, [state.loadUpdateAlarm, emitEvent, t]);

  /**
   * Met à jour les tables automatiques
   */
  const updateAutoTableAsync = useCallback(async () => {
    if (state.loadUpdateAutoTable) {
      emitEvent?.("alert", {
        text: t("navigation.tools.updateautotable.alreadyinprogress")
      });
    } else {
      setState(prev => ({ ...prev, loadUpdateAutoTable: true }));
      
      try {
        await postAsync("/autoTableUpdate", null, null, null, {
                 onSuccess: (result) => {
                   setState(prev => ({ ...prev, loadUpdateAutoTable: false }));
                   emitEvent?.("alert", { text: t("navigation.tools.updateautotable.done") });
                 },
                 onError: (error) => {
                   setState(prev => ({ ...prev, loadUpdateAutoTable: false }));
                   emitEvent?.("alert", { text: t("navigation.tools.updateautotable.error") });
                 }
        }, {
          maxAttempts: 300, // 300 tentatives × 5 secondes = 25 minutes
          interval: 5000,    // 5 secondes entre chaque vérification
          timeout: 300000      // 300000 ms = 5 minutes
        });
      } catch (error) {
        console.error("❌ Erreur lors de la mise à jour des tables automatiques:", error);
        setState(prev => ({ ...prev, loadUpdateAutoTable: false }));
        emitEvent?.("alert", {
          text: t("navigation.tools.updateautotable.error")
        });
      }
    }
  }, [state.loadUpdateAutoTable, emitEvent, t]);

  /**
   * Teste l'envoi d'email d'alarme
   */
  const testSendAlarmEmail = useCallback(async () => {
    if (state.loadSendAlarmEmail) {
      emitEvent?.("alert", {
        text: t("navigation.tools.testsendalarmemail.alreadyinprogress")
      });
    } else {
      setState(prev => ({ ...prev, loadSendAlarmEmail: true }));
      
      try {
        await postAsync("/testSendAlarmEmail", null, null, null, {
                 onSuccess: (result) => {
                   setState(prev => ({ ...prev, loadSendAlarmEmail: false }));
                   emitEvent?.("alert", { text: t("navigation.tools.testsendalarmemail.done") });
                 },
                 onError: (error) => {
                   setState(prev => ({ ...prev, loadSendAlarmEmail: false }));
                   emitEvent?.("alert", { text: t("navigation.tools.testsendalarmemail.error") });
                 }
        }, {
          maxAttempts: 300, // 300 tentatives × 5 secondes = 25 minutes
          interval: 5000,    // 5 secondes entre chaque vérification
          timeout: 300000      // 300000 ms = 5 minutes
        });
      } catch (error) {
        console.error("❌ Erreur lors de l'envoi de l'email d'alarme:", error);
        setState(prev => ({ ...prev, loadSendAlarmEmail: false }));
        emitEvent?.("alert", {
          text: t("navigation.tools.testsendalarmemail.error")
        });
      }
    }
  }, [state.loadSendAlarmEmail, emitEvent, t]);

  /**
   * Retourne les données de l'icône appropriée selon le type
   */
  const getIconData = useCallback((IconComponent) => {
    return {
      component: IconComponent,
      size: 24
    };
  }, []);

  /**
   * Fonction de compatibilité pour rendre les icônes (à utiliser dans les composants)
   */
  const getIconTag = useCallback((IconComponent) => {
    return <IconComponent size={24} />;
  }, []);

  return {
    state,
    handleMenu,
    handleWindowClick,
    getIconData,
    getIconTag,
    updateAlarmAsync,
    updateAutoMeterAsync,
    updateAutoTableAsync,
    updateGeoLocation,
    testSendAlarmEmail
  };
};

/**
 * Détermine les options de compte selon le type d'utilisateur
 * 
 * @param {Object} me - Utilisateur connecté
 * @param {Function} t - Fonction de traduction
 * @param {Function} getIconTag - Fonction pour obtenir les icônes
 * @returns {Array} Options de compte
 */
export const getAccountOptions = (me, t, getIconTag) => {
  const accountOptions = [
    { icon: getIconTag(SmileyIcon), label: t("navigation.myprofil"), link: "/profile" }
  ];
  
  if (me && me.type < 40) {
    accountOptions.push({
      icon: getIconTag(BellIcon),
      label: t("navigation.mynotification"),
      link: "/setnotification"
    });
  }

  if (me && me.type >= 40) {
    accountOptions.push({
      icon: getIconTag(BellIcon),
      label: t("navigation.mynotification"),
      link: "/setnotificationuser"
    });
  }

  if (me && me.type < 20) {
    // Templates d'email pour admins et superadmins
    accountOptions.push({
      icon: getIconTag(BeakerIcon),
      label: t("navigation.tools.emailtemplates"),
      link: "/tools/email-templates"
    });

    // Paramétrage applicatif réservé au superadmin
    if (me.type === 0) {
      accountOptions.push({
        icon: getIconTag(GearIcon),
        label: t("navigation.adminsettings"),
        link: "/admin/settings"
      });
      accountOptions.push({
        icon: getIconTag(GearIcon),
        label: t("navigation.adminremoteaccess"),
        link: "/admin/remoteaccess"
      });
    }
  }
  
  accountOptions.push({
    icon: getIconTag(SignOutIcon),
    label: t("navigation.disconnect"),
    link: "/logout"
  });
  
  return accountOptions;
};

/**
 * Détermine si un menu doit être affiché selon le type d'utilisateur
 * 
 * @param {string} menuKey - Clé du menu
 * @param {Object} me - Utilisateur connecté
 * @returns {boolean} True si le menu doit être affiché
 */
export const shouldShowMenu = (menuKey, me) => {
  if (!me) return false;
  
  switch (menuKey) {
    case 'admin':
      return me.type === 0;
    case 'buildManager':
      return me.type < 21;
    case 'technicalManager':
      return me.type < 20 || me.type === 31;
    case 'user':
      return me.type < 20;
    case 'housing':
      return true; // Le menu housing est affiché pour tous les utilisateurs connectés
    case 'tools':
      return me.type < 20;
    case 'account':
      return true;
    default:
      return false;
  }
};

/**
 * Détermine si une action est en cours
 * 
 * @param {Array} loadUpdate - Tableau des actions en cours
 * @returns {boolean} True si une action est en cours
 */
export const ActionInProgress = (loadUpdate = []) => {
  return loadUpdate.some(load => load);
};
